// ---------------------------------------------------------------------------
// Top-level error boundary.
//
// React's error boundaries catch render-phase exceptions and event-handler
// exceptions that bubble. They DO NOT catch async/Promise rejections
// (`window.addEventListener('unhandledrejection', ...)` covers those) nor
// errors inside event handlers that don't bubble. So this is necessary but
// not sufficient — global handlers are also installed in index.js.
//
// In production we surface a recovery action ("Reload") rather than a
// developer-style stack trace. The error is logged to console.error so a
// browser-monitoring backend (CloudWatch RUM / Sentry / etc.) can capture it
// if/when wired, but the message is never rendered to the user — leaking
// internals is a real-world risk on a Cognito-authenticated portal.
//
// Ports ui/admin-app/src/components/ErrorBoundary.tsx (MR #6) into plain JS.
// Keep the two implementations in sync until the N=3 extraction trigger
// fires and they move to a shared @vectros-ai/react library.
// ---------------------------------------------------------------------------

import React, { Component } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Single sink for render-phase errors. console.error is the contract a
    // future monitoring hook would tap into. We deliberately do NOT render
    // the message — users should not see internal details.
    console.error('[dev-portal] render error', error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Stack spacing={3} alignItems="flex-start">
          <Typography variant="h4" component="h1">
            Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary">
            An unexpected error occurred. Please reload to continue.
          </Typography>
          <Box>
            <Button variant="contained" onClick={this.handleReload}>
              Reload
            </Button>
          </Box>
        </Stack>
      </Container>
    );
  }
}
